import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  BookOpen,
  Target,
  HelpCircle,
  Brain,
  FileText,
  Settings
} from 'lucide-react';
import './NavigationSidebar.css';

const NavigationSidebar = ({ activeTab, onTabChange, isStudying }) => {
  const [isOpen, setIsOpen] = useState(!isStudying);
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-collapse when studying starts, auto-expand when not studying
  useEffect(() => {
    if (!isMobile) {
      setIsOpen(!isStudying);
    }
  }, [isStudying, isMobile]);

  // Handle click outside to close sidebar on desktop
  useEffect(() => {
    if (!isMobile && isOpen) {
      const handleClickOutside = (event) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobile, isOpen]);

  const tabs = [
    { id: 'study-plan', label: 'Study Plan', icon: Target, color: '#3b82f6' }, // blue-500
    { id: 'guide', label: 'Guide', icon: BookOpen, color: '#10b981' }, // emerald-500
    { id: 'questions', label: 'Questions', icon: HelpCircle, color: '#f59e0b' }, // amber-500
    { id: 'study-materials', label: 'Study Materials', icon: Brain, color: '#8b5cf6' }, // violet-500
    { id: 'files', label: 'Files', icon: FileText, color: '#ef4444' }, // red-500
    { id: 'settings', label: 'Settings', icon: Settings, color: '#6b7280' } // gray-500
  ];

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button 
          className={`mobile-nav-toggle ${isOpen ? 'open' : ''}`}
          onClick={toggleSidebar}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <motion.aside 
          ref={sidebarRef}
          className={`navigation-sidebar desktop ${isOpen ? 'open' : 'collapsed'}`}
          initial={false}
          animate={{ width: isOpen ? 280 : 60 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div className="sidebar-header">
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              <Menu size={20} />
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.h3
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  Navigation
                </motion.h3>
              )}
            </AnimatePresence>
          </div>
          
          <nav className="sidebar-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.id)}
                  title={!isOpen ? tab.label : ''}
                  data-tour={`${tab.id}-tab`}
                >
                  <Icon size={20} style={{ color: isActive ? 'white' : tab.color }} />
                  <AnimatePresence>
                    {isOpen && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {tab.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </nav>
        </motion.aside>
      )}

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <>
            <motion.div 
              className="mobile-nav-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.aside 
              className="navigation-sidebar mobile"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="sidebar-header">
                <h3>Navigation</h3>
                <button className="sidebar-close" onClick={() => setIsOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              
              <nav className="sidebar-nav">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      className={`nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleTabClick(tab.id)}
                      data-tour={`${tab.id}-tab`}
                    >
                      <Icon size={20} style={{ color: isActive ? 'white' : tab.color }} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavigationSidebar;